import { useEffect, useRef, useState } from "react";

import Button from "@/design-system/button";
import Goodie from "@/design-system/icons/goodie";
import { useViewportInfoContext } from "@/src/contexts";
import { useSearchNavigation } from "@/src/contexts/useSearchNavigation";
import type { IMobileAppInstallation } from "@/src/mobile/MobileAppInstallation";
import type { ICampaignBar } from "@/src/shared/CampaignBar";
import HeaderLinks from "@/src/shared/Header/header-links";
import Menu from "@/src/shared/Header/menu";
import { lazyLoadComponentFactory } from "@Components/Modules/Helpers";
import { mainHeaderId, searchNavigationId } from "@Constants/PortalConstants";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QueryXsMax, colors } from "../../../styles/tokens.stylex";
import HeaderSearch from "./header-search";
import HeaderSearchMobile from "./header-search-mobile";

const MobileAppInstallation = lazyLoadComponentFactory(
    () => import(/* webpackChunkName: "mobile-app-installation" */ "../../mobile/MobileAppInstallation"),
);

export interface IHeader {
    campaignBar?: ICampaignBar;
    mobileAppInstallation?: IMobileAppInstallation;
}

export default function Header({ mobileAppInstallation }: IHeader) {
    const { isMobile } = useViewportInfoContext();
    const [isStuck, setIsStuck] = useState(false);

    const showSearchNavigation = useSearchNavigation();
    const isStuckRef = useRef(isStuck);

    isStuckRef.current = isStuck;

    const onScroll = () => {
        const isScrolled = window.scrollY > 25;

        if (isScrolled !== isStuckRef.current) {
            setIsStuck(isScrolled);
        }
    };

    useEffect(() => {
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header id={mainHeaderId} {...stylex.props(styles.header, isStuck && styles.shadow)}>
            {isMobile && mobileAppInstallation && !showSearchNavigation && (
                <MobileAppInstallation {...mobileAppInstallation} />
            )}
            <div key={String(isMobile)} {...stylex.props(styles.top, isMobile && styles.topMobile)}>
                <Button
                    variant="none"
                    as="a"
                    href="/"
                    aria-label="Homepage"
                    size="None"
                    styleX={[styles.logo, isMobile && styles.logoMobile]}
                    data-testid="hdr-logo"
                >
                    <Goodie />
                </Button>
                {!isMobile && <HeaderSearch />}
                <Menu styleX={styles.menu} />
                {isMobile && <HeaderSearchMobile />}
            </div>
            <HeaderLinks />
            <div id={searchNavigationId} {...stylex.props(styles.searchNav)} />
        </header>
    );
}

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    header: {
        gap: ComposableSpacing.s1,

        transition: "box-shadow 0.15s",
        backgroundColor: colors.bg_Primary,
        boxShadow: `${colors.bg_Primary} 0px 0px 5px 0px`,
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        zIndex: 1,
        top: 0,
        width: "100%",
    },
    top: {
        gap: {
            default: "1rem",
            [xsMax]: ComposableSpacing.s2,
        },
        alignItems: "center",
        display: "grid",
        gridTemplateColumns: {
            default: "minmax(100px, 1fr) minmax(max-content, 30%) minmax(max-content, 1fr)",
            [xsMax]: "minmax(100px, 1fr) minmax(max-content, 30%) 40px",
        },
        gridTemplateRows: "1fr",
        paddingBottom: "0",
        paddingLeft: "30px",
        paddingRight: "30px",
        paddingTop: "10px",
    },
    searchNav: {
        display: { default: "block", [":empty"]: "none" },
    },
    topMobile: {
        paddingBottom: "0",
        paddingLeft: "10px",
        paddingRight: "10px",
        paddingTop: "10px",
    },
    logo: {
        padding: 0,
        justifySelf: "left",
        height: "50px",
    },
    logoMobile: {
        flexShrink: 0,
        width: "100px",
    },
    shadow: {
        boxShadow: "rgb(180, 180, 180) 0px 0px 5px 0px",
    },
    menu: {
        flexGrow: 1,
    },
});
